import { Box, IconButton, TableRow } from '@mui/material';
import { ExternalLink } from 'lucide-react';
import { useIntl } from 'react-intl';
import type {
    ClinicalPeopleList,
    PatientSummaryDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import { useIsOpeningInViewer, useOpenInViewer } from '~/hooks/openInViewer';
import {
    patientCourseStagesDone,
    resolveCourseStage,
    type ClinicalLists,
} from '../model';
import { PatientListBodyCell } from '../PatientList/PatientListTable';
import {
    CourseStageSummary,
    OpenButtonHint,
    PatientCare,
    PatientIdentity,
    ReleaseButton,
    TreatmentStart,
} from '../shared';

type TaskRowProps = {
    patient: PatientSummaryDto;
    lists: ClinicalLists;
    people: ClinicalPeopleList | undefined;
    /** Whether the user may assign patients; releasing it is offered only then. */
    canAssign: boolean;
    /** Today, injected so the treatment start note is testable. */
    today: Date;
};

/**
 * One patient assigned to the user: who, where, when treatment starts, where
 * the workflow is, then opening the patient and releasing it.
 */
export default function TaskRow({
    patient,
    lists,
    people,
    canAssign,
    today,
}: TaskRowProps) {
    const intl = useIntl();
    const patientId = patient.id;
    const openInViewer = useOpenInViewer();
    const openBusy = useIsOpeningInViewer();

    return (
        <TableRow>
            <PatientListBodyCell>
                <PatientIdentity
                    patient={patient}
                    today={today}
                    tags={lists.tags}
                />
            </PatientListBodyCell>
            <PatientListBodyCell>
                <PatientCare patient={patient} lists={lists} people={people} />
            </PatientListBodyCell>
            <PatientListBodyCell>
                <TreatmentStart patient={patient} today={today} />
            </PatientListBodyCell>
            <PatientListBodyCell>
                <CourseStageSummary
                    stage={resolveCourseStage(patient.courseStage)}
                    done={patientCourseStagesDone(patient.courseStagesDone)}
                />
            </PatientListBodyCell>
            <PatientListBodyCell alignEnd>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <OpenButtonHint
                        enabled={!openBusy && patientId !== undefined}
                        title={intl.formatMessage({
                            id: openBusy
                                ? 'patientList.open.inFlight'
                                : 'patientList.openHint',
                        })}
                    >
                        <IconButton
                            size="small"
                            aria-label={intl.formatMessage({
                                id: 'patientList.row.openPatient',
                            })}
                            disabled={openBusy || patientId === undefined}
                            onClick={() => {
                                if (patientId !== undefined) {
                                    // On the work case where the patient has one.
                                    openInViewer(patientId, 'all', {
                                        workCase: true,
                                    });
                                }
                            }}
                        >
                            <ExternalLink size={16} />
                        </IconButton>
                    </OpenButtonHint>
                    {canAssign && <ReleaseButton patient={patient} />}
                </Box>
            </PatientListBodyCell>
        </TableRow>
    );
}
